import React from "react";
import { Table, Tag, Space, Button, Popconfirm, Tooltip } from "antd";
import {
  EyeOutlined,
  DeleteOutlined,
  SendOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

const STATUS_COLORS = {
  Draft: "default",
  "Pending Head Teacher Approval": "orange",
  "Pending Deputy Principal Approval": "blue",
  "Pending Principal Approval": "purple",
  Published: "green",
};

const CurricularCalendarTable = ({
  calendars = [],
  loading,
  isAdmin,
  onView,
  onSubmit,
  onApprove,
  onReject,
  onDelete,
}) => {
  const columns = [
    {
      title: "Term",
      dataIndex: "termName",
      key: "termName",
      sorter: (a, b) => (a.termName || "").localeCompare(b.termName || ""),
    },
    {
      title: "Academic Year",
      dataIndex: "academicYear",
      key: "academicYear",
      sorter: (a, b) =>
        (a.academicYear || "").localeCompare(b.academicYear || ""),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || "default"}>{status}</Tag>
      ),
    },
    {
      title: "Events",
      dataIndex: "eventCount",
      key: "eventCount",
      align: "center",
      render: (count) => <Tag color="blue">{count}</Tag>,
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      key: "createdBy",
      render: (v) => v || "-",
    },
    {
      title: "Published",
      dataIndex: "publishedAt",
      key: "publishedAt",
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 160,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => onView(record)}
            />
          </Tooltip>

          {isAdmin && record.status === "Draft" && (
            <Tooltip title="Submit for Approval">
              <Popconfirm
                title="Submit this calendar for approval?"
                description="It must have at least one event."
                onConfirm={() => onSubmit(record.id)}
                okText="Submit"
                cancelText="Cancel"
              >
                <Button
                  type="text"
                  style={{ color: "#1677ff" }}
                  icon={<SendOutlined />}
                />
              </Popconfirm>
            </Tooltip>
          )}

          {isAdmin &&
            record.status !== "Draft" &&
            record.status !== "Published" && (
              <>
                <Tooltip title="Approve">
                  <Popconfirm
                    title="Approve this calendar?"
                    onConfirm={() => onApprove(record.id)}
                    okText="Approve"
                    cancelText="Cancel"
                  >
                    <Button
                      type="text"
                      style={{ color: "#52c41a" }}
                      icon={<CheckCircleOutlined />}
                    />
                  </Popconfirm>
                </Tooltip>
                <Tooltip title="Reject">
                  <Button
                    type="text"
                    style={{ color: "#faad14" }}
                    icon={<CloseCircleOutlined />}
                    onClick={() => onReject(record)}
                  />
                </Tooltip>
              </>
            )}

          {isAdmin && record.status === "Draft" && (
            <Tooltip title="Delete">
              <Popconfirm
                title="Delete this calendar?"
                description="This cannot be undone."
                onConfirm={() => onDelete(record.id)}
                okText="Delete"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
              >
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={calendars}
      rowKey="id"
      loading={loading}
      size="small"
      scroll={{ x: 900 }}
      pagination={{
        pageSize: 15,
        showSizeChanger: true,
        pageSizeOptions: ["10", "15", "30"],
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} of ${total} calendars`,
      }}
    />
  );
};

export default CurricularCalendarTable;
